import {
  Box,
  Heading,
  IconButton,
  Spinner,
  Text,
  VStack
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import Pathicon from "@pathwright/web/src/modules/pathicon/Pathicon"
import { ReactNode } from "react"
import PublishButton from "./CardButton"
import CardTip, { SyncCardTipId } from "./CardTip"
import ChangeList from "./ChangeList"
import { useSyncCard } from "./SyncCardProvider"
import { useCanDesignSourcePath, useShowSyncOption } from "./utils"

export type CardTemplateProps = {
  title: string | null
  description: string
  icon?: IconName | null
  published?: boolean
  renderBody?: () => ReactNode
  renderButton?: () => ReactNode
  enableButton?: boolean
  showMeta?: boolean
  tipId?: SyncCardTipId | null
  renderTip?: () => ReactNode
  allowShowSync?: boolean
}

const CardTemplate = (props: CardTemplateProps) => {
  let {
    title,
    description,
    icon,
    renderBody,
    renderButton,
    enableButton,
    tipId,
    renderTip,
    allowShowSync = false
  } = props

  const { loading, syncDir, onSetSyncDir, fromCohort, shouldExpandTips } =
    useSyncCard()
  const canDesignSourcePath = useCanDesignSourcePath({
    cohortId: fromCohort.id
  })

  // Get tip based on user design perms.
  tipId ||=
    syncDir === "forward"
      ? canDesignSourcePath
        ? "more-about-publishing-as-designer"
        : "more-about-publishing-as-teacher"
      : canDesignSourcePath
      ? "more-about-syncing-as-designer"
      : // : "more-about-syncing-as-teacher"
        null // Teachers can't use sync.

  const showSync =
    allowShowSync &&
    useShowSyncOption({
      cohortId: fromCohort.id
    })

  return (
    <VStack
      gap={"syncMd"}
      px={"syncLarge"}
      pb={"syncLarge"}
      pt={"syncXLarge"}
      borderRadius="card"
      bg="white"
      className="SyncCard__card-wrapper"
      pos="relative"
      zIndex={1}
      w={[340, 500]}
    >
      <VStack gap={0} py={0} w="100%">
        {showSync ? (
          <Box pos="absolute" top={2} right={1}>
            <IconButton
              aria-label="sync-back"
              icon={<Pathicon icon="recur" color="textOnLight" size="1.5em" />}
              variant="ghost"
              bgColor="transparent"
              _hover={{
                bgColor: "transparent",
                opacity: 0.8
              }}
              onClick={() => {
                onSetSyncDir!(syncDir === "forward" ? "backward" : "forward")
              }}
            />
          </Box>
        ) : null}
        {loading ? (
          <VStack justify="center" align="center" height={10} width={10}>
            <Spinner size="lg" color="textOnLight" />
          </VStack>
        ) : icon !== null ? (
          <Pathicon
            icon={icon || ("cloud-notification-fill" as IconName)}
            color="textOnLight"
            minW={10}
            h={10}
            w={10}
          />
        ) : null}
        {title !== null && (
          <Heading as="h3" size="md" my={0} color="textOnLight">
            {title}
          </Heading>
        )}
        <Box my={0} maxW="300px">
          <Text
            align="center"
            whiteSpace="wrap"
            color="textOnLight"
            my={0}
            fontSize="14px"
            lineHeight={"20px"}
            dangerouslySetInnerHTML={{
              __html: description
            }}
          />
        </Box>
      </VStack>
      {renderTip ? (
        renderTip()
      ) : (
        <CardTip
          tipId={tipId}
          defaultState={shouldExpandTips ? "expanded" : "collapsed"}
        />
      )}
      {renderBody ? renderBody() : <ChangeList />}
      <VStack gap={2} p={0} className="SyncCard__card-button-wrapper">
        {renderButton ? (
          renderButton()
        ) : (
          <PublishButton disabled={!enableButton} />
        )}
        {/* {showMeta && syncDir !== SYNC_DIR_BACKWARD && <PublishMeta />} */}
      </VStack>
    </VStack>
  )
}

export default CardTemplate
